import { useEffect } from 'react';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';

/**
 * Hook to enable Shift+Tab reset behavior in form inputs
 *
 * When a user presses Shift+Tab on an input/textarea field, focus moves to the
 * first focusable element within the same form/modal instead of moving backward in tab order.
 * This creates a circular navigation pattern.
 *
 * Can be disabled via useGlobalBehavior().setShiftTabResetEnabled(false)
 *
 * Usage:
 * - Add this hook to your root layout or main app component
 * - It will automatically work for all input/textarea elements
 *
 * @example
 * function MyComponent() {
 *   useShiftTabReset();
 *   return <YourApp />;
 * }
 */
export const useShiftTabReset = () => {
  const { shiftTabResetEnabled } = useGlobalBehavior();
  useEffect(() => {
    // Skip if behavior is disabled
    if (!shiftTabResetEnabled) {
      return;
    }

    const handleShiftTab = (event: KeyboardEvent) => {
      // Check if the target is an input or textarea
      const target = event.target as HTMLElement;
      const isInputElement =
        target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

      // Only handle Shift+Tab combination
      if (event.key !== 'Tab' || !event.shiftKey || !isInputElement) {
        return;
      }

      // Prevent default tab behavior
      event.preventDefault();

      // Determine search scope
      let searchContainer = document.body;

      // Check if we're inside a modal (highest priority)
      const modal = document.querySelector('.ant-modal-content, [role="dialog"]');
      if (modal && modal.contains(target)) {
        searchContainer = modal as HTMLElement;
      }
      // Otherwise, check if we're inside a form element
      else {
        const parentForm = target.closest('form');
        if (parentForm) {
          searchContainer = parentForm;
        }
      }

      // Get all focusable elements within the container
      const focusableElements = Array.from(
        searchContainer.querySelectorAll<HTMLElement>(
          'input, textarea, button, select, a[href], [tabindex]:not([tabindex="-1"])'
        )
      );

      // Filter to only visible, enabled elements
      const visibleFocusableElements = focusableElements.filter(el => {
        // Skip hidden elements
        if (el.offsetParent === null) {
          return false;
        }
        // Skip disabled inputs
        if (
          (el instanceof HTMLInputElement ||
            el instanceof HTMLTextAreaElement ||
            el instanceof HTMLButtonElement ||
            el instanceof HTMLSelectElement) &&
          el.disabled
        ) {
          return false;
        }
        return true;
      });

      // Focus the first input/textarea/select element (form elements only)
      const firstFormElement = visibleFocusableElements.find(
        el =>
          el instanceof HTMLInputElement ||
          el instanceof HTMLTextAreaElement ||
          el instanceof HTMLSelectElement
      );

      if (firstFormElement) {
        firstFormElement.focus();

        // If it's an input or textarea, select all text for better UX
        if (
          firstFormElement instanceof HTMLInputElement ||
          firstFormElement instanceof HTMLTextAreaElement
        ) {
          firstFormElement.select?.();
        }
      }
    };

    // Attach event listener to the document
    document.addEventListener('keydown', handleShiftTab);

    return () => {
      document.removeEventListener('keydown', handleShiftTab);
    };
  }, [shiftTabResetEnabled]);
};
